import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import DeleteIcon from "@mui/icons-material/Delete";
import { NotificationContext } from "../../context/NotificationContext";
import { AuthContext } from "../../context/AuthContext";
import { messagingApi } from "../../api/messagingApi";
import Layout from "../../components/layout/Layout";

const NotificationsPage = () => {
  const {
    notifications,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    fetchUnreadCount,
  } = useContext(NotificationContext);
  const { userId } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadNotifications = async () => {
      if (userId) {
        setLoading(true);
        setError(null);
        try {
          await fetchNotifications();
        } catch (err) {
          console.error("Failed to load notifications:", err);
          setError("Failed to load notifications.");
        } finally {
          setLoading(false);
        }
      }
    };

    loadNotifications();
  }, [userId, fetchNotifications]);

  const handleMarkOneAsRead = async (notificationId) => {
    if (typeof markAsRead === "function") {
      try {
        await markAsRead(notificationId);
      } catch (err) {
        console.error("Error marking one as read:", err);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    if (typeof markAllAsRead === "function") {
      try {
        await markAllAsRead();
        fetchUnreadCount(); // Update unread count immediately
      } catch (err) {
        console.error("Error marking all as read:", err);
      }
    }
  };

  const handleClearAllNotifications = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      await clearAllNotifications();
      fetchUnreadCount(); // Update unread count
    } catch (err) {
      console.error("Error clearing all notifications:", err);
      setError("Failed to clear all notifications.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
    } catch (err) {
      console.error("Error deleting notification:", err);
    }
  };

  const getNotificationColor = (type) => {
    return (
      {
        message: "#2196F3", // Blue
        invitation: "#FF9800", // Orange
        application: "#4CAF50", // Green
        approval: "#8BC34A", // Light Green
        rejection: "#F44336", // Red
      }[type] || "#757575"
    ); // Grey for unknown
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "Invalid Date" : date.toLocaleString();
  };

  return (
    <Layout>
      <div>
        <Box
          sx={{
            maxWidth: { xs: "90%", md: "70%" },
            margin: "auto",
            pt: { xs: 2, md: 3 },
            pr: { xs: 0, md: 3 },
            pb: { xs: 2, md: 3 },
            pl: { xs: 2, md: 3 },
          }}
        >
          <Paper
            elevation={3}
            sx={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <Box
              sx={{
                p: 2,
                backgroundColor: "#6E0506",
                color: "#fff",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: "center",
                 gap: { xs: 1, sm: 0 },
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                All Notifications
              </Typography>
              <Box>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<MarkEmailReadIcon />}
                  onClick={handleMarkAllAsRead}
                  disabled={
                    loading || !notifications || notifications.length === 0
                  }
                  sx={{
                    mr: 1,
                    color: "#fff",
                    borderColor: "#fff",

                    "&:hover": {
                      borderColor: "#fff",
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },

                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.6)",
                      borderColor: "rgba(255,255,255,0.4)",
                    },
                  }}
                >
                  Mark All Read
                </Button>
              </Box>
            </Box>
            <Divider />

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 200,
                }}
              >
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box sx={{ p: 3, textAlign: "center", color: "error.main" }}>
                <Typography>{error}</Typography>
              </Box>
            ) : notifications && notifications.length > 0 ? (
              <List>
                {notifications.map((notification, index) => (
                  <React.Fragment key={notification.id || index}>
                    <ListItem
                      sx={{
                        backgroundColor: notification.isRead
                          ? "transparent"
                          : "#e3f2fd",
                        borderLeft: `5px solid ${getNotificationColor(notification.type)}`,
                        "&:hover": { backgroundColor: "#f0f0f0" },
                        py: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: notification.isRead ? 400 : 600 }}
                          >
                            {notification.message}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(notification.createdAt)}
                          </Typography>
                        }
                      />
                      {!notification.isRead && (
                        <IconButton
                          edge="end"
                          aria-label="mark as read"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkOneAsRead(notification.id);
                          }}
                          sx={{ mt: 0.5 }}
                        >
                          <MarkEmailReadIcon fontSize="small" color="primary" />
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNotification(notification.id);
                        }}
                        sx={{ mt: 0.5, ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </ListItem>
                    {index < notifications.length - 1 && (
                      <Divider component="li" />
                    )}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                <NotificationsOffIcon sx={{ fontSize: 50, mb: 1 }} />
                <Typography variant="h6">No Notifications</Typography>
                <Typography variant="body2">You're all caught up!</Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </div>
    </Layout>
  );
};

export default NotificationsPage;
